import React, { JSXElementConstructor, useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { Item, ItemContainer, ItemPrimary, ItemAccessory } from '@components/Modals/Actions';
import { deleteReconnect, reconnectCadenceSelector, setReconnect } from '@actions/reconnect';
import {
  ReconnectMoIcon,
  ReconnectQrIcon,
  ReconnectWkIcon,
  ReconnectYrIcon,
  XIcon,
} from '@components/Icons';
import Pill from '@components/Pill';
import Cadence, { CadenceType } from '@models/Cadence';
import { CadenceEditor, CadencePreview } from './Cadence';
import { SVGProps } from '@components/Icons/SVGWrapper';
import parseCadence from './parseCadence';
import styles from './Reconnect.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { AutoReconnectIcon, NoReconnectIcon } from '@components/Icons/Reconnect';
import { selectSelectedContact } from '@actions/ui';
import { useHotkeyHandler } from '@hooks/use-hotkey-handler';
import SearchableOptionsModal from '../SearchableOptions';

interface CadenceOptionType {
  title: string;
  type?: CadenceType;
  IconComponent: JSXElementConstructor<SVGProps>;
}

const cadenceTypeIcon = {
  weekly: ReconnectWkIcon,
  monthly: ReconnectMoIcon,
  quarterly: ReconnectQrIcon,
  yearly: ReconnectYrIcon,
  auto: AutoReconnectIcon,
  none: NoReconnectIcon,
};

const options: Array<CadenceOptionType> = [
  'weekly',
  'monthly',
  'quarterly',
  'yearly',
  'auto',
  'none',
].map((cadenceType) => {
  const cadence = new Cadence({ type: cadenceType });

  return {
    type: cadence.type,
    title: cadence.title,
    IconComponent: cadenceTypeIcon[cadence.type],
  };
});

const CadenceOption = ({
  title,
  type,
  cadence,
  selected,
  IconComponent,
  removeSelection,
}: CadenceOptionType & { cadence: Cadence; selected: boolean; removeSelection?: () => void }) => {
  return (
    <ItemContainer>
      <ItemPrimary className={cn(styles.item, selected && styles.selected)}>
        <IconComponent className={styles.icon} />
        <span className={styles.cadenceTitle}>{title}</span>
      </ItemPrimary>
      <ItemAccessory className="text-small modal-actions-item-accessory">
        <CadencePreview cadence={cadence} type={type} />
      </ItemAccessory>
      {removeSelection && (
        <div onClick={removeSelection} className={styles.dismissBtn}>
          <XIcon width={7} height={7} />
        </div>
      )}
    </ItemContainer>
  );
};

export default function ReconnectModal({ closeModal }) {
  const dispatch = useDispatch();
  const currentSelectedContact = useSelector(selectSelectedContact);
  // We memoize selectedContact on mount in case the App UI updates while the modal is open. This can
  // happen, for instance, if a selectedContact is dropped out of the inbox when an inbox GET resolves
  // after the modal has been popped.
  const selectedContact = useMemo(() => currentSelectedContact, []);
  const cadenceInStore = useSelector(reconnectCadenceSelector(selectedContact.id));
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCadence, setSelectedCadence] = useState<Cadence | null>(null);
  // Cadence that we infer from current search term
  const [speculativeCadence, setSpeculativeCadence] = useState<Cadence | null>(null);
  const [filteredOptions, setFilteredOptions] = useState(options);

  const renderOption = useCallback(
    (option) => {
      const selected = cadenceInStore.type == option.type;
      let cadence = speculativeCadence;

      if (selected && !cadence) {
        cadence = cadenceInStore;
      }

      return <CadenceOption {...option} cadence={cadence} selected={selected} />;
    },
    [speculativeCadence, cadenceInStore],
  );

  const renderedOptions = useMemo(
    () => (selectedCadence ? [] : filteredOptions),
    [filteredOptions, selectedCadence],
  );

  const handleSearch = (searchTerm: string) => {
    const { cadence, cadenceType } = parseCadence(searchTerm);

    if (
      (cadence && !speculativeCadence) ||
      (!cadence && speculativeCadence) ||
      (cadence && speculativeCadence && cadence.string != speculativeCadence.string)
    ) {
      setSpeculativeCadence(cadence);
    }

    if (cadenceType) {
      return setFilteredOptions(options.filter(({ type }) => type == cadenceType));
    }

    if (filteredOptions.length !== options.length) {
      setFilteredOptions(options);
    }
  };

  const onOptionSelected = (option) => {
    if (speculativeCadence) {
      setSelectedCadence(speculativeCadence.copyWith({ type: option.type }));
      return setSpeculativeCadence(null);
    } else if (!speculativeCadence && option.type == cadenceInStore.type) {
      setSelectedCadence(cadenceInStore.copyWith({}));
      return setSpeculativeCadence(null);
    }

    setSelectedCadence(new Cadence(option));
  };

  useEffect(() => {
    if (selectedCadence && cadenceInStore.string != selectedCadence.string) {
      if (selectedCadence.type == 'none') {
        dispatch(deleteReconnect({ contactID: selectedContact.id }));
      } else {
        dispatch(setReconnect({ contactID: selectedContact.id, cadence: selectedCadence }));
      }
    }
  }, [selectedCadence]);

  useHotkeyHandler(
    {
      handler: (e) => {
        if (e.key == 'Escape' || (selectedCadence && e.key == 'Enter')) {
          closeModal();
        }

        e.preventDefault();
      },
      priority: 1,
    },
    [selectedCadence],
  );

  return (
    <SearchableOptionsModal
      overlayClassName={styles.modalOverlay}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      closeModal={closeModal}
      optionsTitle={`Cadence - ${cadenceInStore.title}${cadenceInStore.type == 'none' ? 'd' : ''}`}
      alternateInput={
        selectedCadence && (
          <CadenceEditor cadence={selectedCadence} setCadence={setSelectedCadence} />
        )
      }
      afterInput={<Pill contact={selectedContact} maxWidth={160} />}
      options={renderedOptions}
      renderOption={renderOption}
      handleSearch={handleSearch}
      onOptionSelected={onOptionSelected}
      afterOptions={
        selectedCadence && (
          <Item className={styles.selectedOptionContainer}>
            <CadenceOption
              title={selectedCadence.title}
              type={selectedCadence.type}
              IconComponent={cadenceTypeIcon[selectedCadence.type]}
              cadence={selectedCadence}
              removeSelection={() => setSelectedCadence(null)}
              selected={false}
            />
          </Item>
        )
      }
    />
  );
}
